import React, { useCallback, useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { FileUploader } from 'react-drag-drop-files';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup, IconButton, Radio, RadioGroup, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { aiTransformActions, authActions, uploadFileWithPreview } from 'store';
import { useAiSessionNoteStream } from '../../../utils/hooks/useAiSessionNoteStream';
import { sanitizeAiHtml } from '../../../utils/sanitizeAiHtml';
import { AiAssistPopover, AiAssistPhase } from './aiAssistPopover';
import UndoIcon from '@mui/icons-material/Undo';
import { DateCalendarForm, Input, Loader, MaskInputCustom, MinLoader, TimeInput, SelectAutocompleteMultiple } from 'components';
import { isAutoSignature } from '../formDetail/signatureFill.constants';
import { base64ToFile, FindError, FindLoad, getNestedError, Images, KeyPurpose, OnlyNumber, OrgIntegrations, attributeTypes,
    renderStaffName,
    noteStatusEnums,
    sanitizeHtml,
    User,
    userTypeEnums,
} from 'utils';
import { inputsStyle } from '../../../components/inputs/styles';
import { renderTimes } from '../../../fragments/schedule/core/modals/constants';
import { UploadedSignature } from '../../../fragments/schedule/core/common/uploadedSignature';
import { inputTypeEnums, selectTypeEnums } from '../formDetail/attributes/constnats';
import { buildSelectionRules, selectionRequiredMark } from '../formDetail/minSelections';
import {
    MentionableInput,
    RichTextElement,
    TableElement,
    FormSignature,
    SessionDataTableElement,
    AutoSignatureElement,
    GoalModeRichText,
    GoalModeTable,
} from '../formDetail/formBody/elements';
import { ResizableImage } from '../formDetail/formBody/elements/resizableImage';
import { InlineFieldsRichText } from '../formDetail/formBody/elements/inlineFieldsRichText';
import { useFormBuilder, TargetElement, GraphCharts, formPageEnums } from '../formDetail';
import { getWidth } from '../constants';
import { isPrintableAttribute } from '../utils/printableAttribute';
import { hasInlineFields, inlineFieldsToText, richTextRequiredError, validateRichText } from '../utils/inlineFields';
import { useAutoFillMentions } from '../hooks/useAutoFillMentions';
import { evaluateConditions } from '../utils/evaluateConditions';
import { goalAttributes, goalTemplateSection, isOwnedGoal, keepOwnedGoals, mergeAttribute } from '../utils/goalAttributes';
import { isAttributeFilled, isEditableTable, isFillableAttribute, isReadOnlyBoxType } from '../utils/fillableFields';
import { LockedField } from '../utils/lockedField';
import { resolveRichTextImages } from '../utils/resolveRichTextImages';
import { uploadPendingSignatures } from '../utils/uploadPendingSignatures';
import { PrevFileSignature, PrevUploadedFile } from '../formPreview/elements';
import { PrevHeaderFooterBlock } from '../formPreview';
import { useMentionOptions } from '../formDetail/formBody/elements/useMentionOptions';
import { sectionTitleStyle, showSectionTitle, isContinuousSections } from '../formDetail/section';

const DEFAULT_FORM_ACTION_TYPE = 'GET_FORM_BY_ID';
const FORM_DEFAULT_VALUES = 'GET_FORMS_DEFAULT';

const seedLegacyAiAcceptance = (sections, noteStatus) => {
    if (!Array.isArray(sections) || !noteStatus || noteStatus === noteStatusEnums.DRAFT) return sections;
    return sections.map((section) =>
        Array.isArray(section?.attributes)
            ? {
                  ...section,
                  attributes: section.attributes.map((attr) =>
                      attr?.type === attributeTypes.RICH_TEXT && attr?.properties?.aiSummary && attr?.aiContentAccepted === undefined
                          ? { ...attr, aiContentAccepted: true }
                          : attr,
                  ),
              }
            : section,
    );
};

// `allowReorder`: opt-in. Only the progress-report builder (which also adds/removes fields) may
// drag-reorder; every fill host (session notes, form notes, ABC data, documentation) and every
// detail view stays locked — reordering is a template-authoring action, not a note-filling one.
export const ReportBuilder = forwardRef(({ defaultForm, resourceId, handleSaveNote, fromReport, isDetail, allowReorder = false, statusRef, noteStatus, onSigUploadingChange }, ref) => {
    const {
        handleSubmit,
        watch,
        control,
        setValue,
        getValues,
        clearErrors,
        reset,
        formState: { errors, isDirty },
    } = useForm({ mode: 'onChange', reValidateMode: 'onChange' });
    const classes = inputsStyle();
    const loadForm = FindLoad(DEFAULT_FORM_ACTION_TYPE);
    const loadFormDefaults = FindLoad(FORM_DEFAULT_VALUES);
    const dispatch = useDispatch();
    const aiHttpLoading = FindLoad('GENERATE_AI_SESSION_NOTE');
    const aiStreamStatus = useSelector((state) => state.aiNote?.streamStatus);
    const aiInProgress = !!aiHttpLoading?.length || aiStreamStatus === 'streaming';
    const aiAssistHttpLoading = FindLoad('TRANSFORM_RICH_TEXT');
    const aiTransformConvId = useSelector((state) => state.aiTransform?.transform?.conversationId || null);
    const aiTransformStreamStatus = useSelector((state) => state.aiTransform?.streamStatus);
    /** RHF dotted path of the field that's currently being transformed. */
    const aiTransformPathRef = useRef(null);
    /** Last conversationId we wired the stream to — guards against re-starting on the same id. */
    const lastTransformConvIdRef = useRef(null);
    const [aiAssistOpenPath, setAiAssistOpenPath] = useState(null);
    const [aiAssistTransformingPath, setAiAssistTransformingPath] = useState(null);
    /** Phase + transient state for the open Dialog. */
    const [aiAssistPhase, setAiAssistPhase] = useState(AiAssistPhase.IDLE);
    const [aiAssistPreviewText, setAiAssistPreviewText] = useState('');
    const [aiAssistOriginalSource, setAiAssistOriginalSource] = useState('');
    const [aiAssistLastInstruction, setAiAssistLastInstruction] = useState('');
    const [aiAssistErrorMessage, setAiAssistErrorMessage] = useState('');
    /** Per-path "what was here before AI replaced it" — drives the Revert button.
     *  Cleared on revert (or on next Apply for the same path). */
    const [aiAssistOriginalByPath, setAiAssistOriginalByPath] = useState({});

    /** Streaming chunks land in the popover preview state, NOT the field. */
    const onAiAssistChunk = useCallback(({ content }) => {
        setAiAssistPreviewText(sanitizeAiHtml(content));
    }, []);
    const transformStream = useAiSessionNoteStream({ onUpdate: onAiAssistChunk });
    const stopTransformStreamRef = useRef(transformStream.stop);
    stopTransformStreamRef.current = transformStream.stop;

    useEffect(() => {
        dispatch(authActions.getMyProfile());
    }, []);

    /** Start streaming once the saga returns a conversationId. */
    useEffect(() => {
        if (!aiTransformConvId) return;
        if (lastTransformConvIdRef.current === aiTransformConvId) return;
        lastTransformConvIdRef.current = aiTransformConvId;
        transformStream.start(aiTransformConvId);
    }, [aiTransformConvId, transformStream]);

    /** Stream lifecycle → drive popover phase transitions.
     *  done  → PREVIEW (user reviews & decides)
     *  error → ERROR (with Try Again). */
    useEffect(() => {
        if (transformStream.isDone) {
            dispatch(aiTransformActions.markAiTransformStreamDone());
            setAiAssistTransformingPath(null);
            aiTransformPathRef.current = null;
            // Only flip to PREVIEW if the dialog is still in GENERATING
            // (user might have already closed it).
            setAiAssistPhase((p) =>
                p === AiAssistPhase.GENERATING ? AiAssistPhase.PREVIEW : p,
            );
        } else if (transformStream.isError) {
            dispatch(aiTransformActions.markAiTransformStreamError());
            setAiAssistTransformingPath(null);
            aiTransformPathRef.current = null;
            setAiAssistErrorMessage('AI generation failed mid-stream. Try again.');
            setAiAssistPhase((p) =>
                p === AiAssistPhase.GENERATING ? AiAssistPhase.ERROR : p,
            );
        }
    }, [transformStream.isDone, transformStream.isError, dispatch]);

    /** Reset slice on unmount so a closed-and-reopened modal starts clean. */
    useEffect(() => {
        return () => {
            stopTransformStreamRef.current?.();
            dispatch(aiTransformActions.resetAiTransform());
        };
    }, [dispatch]);

    /** Open the Dialog on a specific field, in IDLE phase. */
    const openAiAssistFor = (path) => {
        const sourceValue = getValues(path) || '';
        setAiAssistOpenPath(path);
        setAiAssistOriginalSource(sourceValue);
        setAiAssistPreviewText('');
        setAiAssistLastInstruction('');
        setAiAssistErrorMessage('');
        setAiAssistPhase(AiAssistPhase.IDLE);
    };

    const closeAiAssist = () => {
        setAiAssistOpenPath(null);
        setAiAssistPhase(AiAssistPhase.IDLE);
        setAiAssistPreviewText('');
        setAiAssistOriginalSource('');
        setAiAssistLastInstruction('');
        setAiAssistErrorMessage('');
    };

    /** "Generate Preview" — kicks off the transform. */
    const handleAiAssistGenerate = (instruction) => {
        if (!aiAssistOpenPath) return;
        const currentValue = getValues(aiAssistOpenPath) || '';
        aiTransformPathRef.current = aiAssistOpenPath;
        setAiAssistTransformingPath(aiAssistOpenPath);
        setAiAssistOriginalSource(currentValue);
        setAiAssistPreviewText('');
        setAiAssistLastInstruction(instruction);
        setAiAssistErrorMessage('');
        setAiAssistPhase(AiAssistPhase.GENERATING);
        dispatch(aiTransformActions.transformRichText(currentValue, instruction));
    };

    /** "Regenerate" — re-runs the same instruction. */
    const handleAiAssistRegenerate = () => {
        if (!aiAssistOpenPath || !aiAssistLastInstruction) return;
        handleAiAssistGenerate(aiAssistLastInstruction);
    };

    /** "Apply Changes" — commits previewText to the field; stores original
     *  for revert. Closes the dialog. */
    const handleAiAssistApply = () => {
        if (!aiAssistOpenPath || !aiAssistPreviewText) return;
        const path = aiAssistOpenPath;
        const original = aiAssistOriginalSource;
        setValue(path, aiAssistPreviewText, { shouldDirty: true, shouldValidate: false });
        setAiAssistOriginalByPath((prev) => ({ ...prev, [path]: original }));
        closeAiAssist();
    };

    /** Revert the most recent AI replacement on `path`. */
    const handleAiAssistRevert = (path) => {
        const original = aiAssistOriginalByPath[path];
        if (original === undefined) return;
        setValue(path, original, { shouldDirty: true, shouldValidate: false });
        setAiAssistOriginalByPath((prev) => {
            const next = { ...prev };
            delete next[path];
            return next;
        });
    };

    /** HTTP-level error (saga TRANSFORM_RICH_TEXT_FAIL → http_requests_on_errors). */
    const aiAssistHttpError = FindError('TRANSFORM_RICH_TEXT');
    useEffect(() => {
        if (!aiAssistHttpError) return;
        if (aiAssistPhase !== AiAssistPhase.GENERATING) return;
        aiTransformPathRef.current = null;
        setAiAssistTransformingPath(null);
        setAiAssistErrorMessage(
            typeof aiAssistHttpError === 'string'
                ? aiAssistHttpError
                : 'Failed to start AI generation. Check your connection and try again.',
        );
        setAiAssistPhase(AiAssistPhase.ERROR);
    }, [aiAssistHttpError, aiAssistPhase]);

    const aiAssistInProgress = !!aiAssistHttpLoading?.length || aiTransformStreamStatus === 'streaming';
    const { aiBridge } = OrgIntegrations();
    const formRef = useRef();
    const sigCanvas = useRef(null);
    // Rendered pixel width of each attribute card, keyed `${sIndex}-${index}`.
    // Frozen onto the card while dragging so @hello-pangea/dnd's fixed-position
    // clone keeps its in-column width instead of expanding to the viewport.
    const itemRefs = useRef({});
    const {
        setFormList,
        formList,
        sectionRefs,
        goalRefs,
        setSelectedIndex,
        setActiveSection,
        selectedIndex,
        activeSection,
        selectedGoalId,
        setSelectedGoalId,
        selectedTargetId,
        setSelectedTargetId,
        activeFormPage,
        onDragStart,
        imageUrls,
        setImageUrls,
        setReportTableCell,
    } = useFormBuilder();
    const [uploadLoader, setUploadLoader] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    // "Show only fields to fill": hides read-only, auto-filled and display blocks while filling.
    const [showOnlyFieldsToFill, setShowOnlyFieldsToFill] = useState(false);
    const applyAutoFill = useAutoFillMentions({ setValue, getValues, formList });
    const isInitializing = useRef(false);
    const isFromWatch = useRef(false);
    const currentModelEnums = useMentionOptions({ activeSection });
    const { userInfo } = User();

    useEffect(() => {
        if (isDetail) return; // no watch sync needed in read-only mode
        const subscription = watch((formData) => {
            if (!isInitializing.current && formData?.sections) {
                isFromWatch.current = true;
                setFormList((prev) => {
                    const mergedSections = (formData.sections || []).map((section, idx) =>
                        keepOwnedGoals(prev?.sections?.[idx], {
                            ...section,
                            selectedGoals: prev?.sections?.[idx]?.selectedGoals || section?.selectedGoals,
                            goalSection: prev?.sections?.[idx]?.goalSection ?? section?.goalSection,
                            groupByDomain: prev?.sections?.[idx]?.groupByDomain ?? section?.groupByDomain,
                            showDomainLabels: prev?.sections?.[idx]?.showDomainLabels ?? section?.showDomainLabels,
                        }),
                    );
                    return {
                        ...prev,
                        sections: mergedSections,
                        attributes: formData.attributes,
                        header: formData?.header ?? prev?.header,
                        footer: formData?.footer ?? prev?.footer,
                    };
                });
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, setFormList]);

    useImperativeHandle(ref, () => ({
        submit: () => {
            handleSubmit(
                (data) => handleCreate(data),
                (errors) => {
                    scrollToFirstError(errors);
                }
            )();
        },
        /** Save Draft — skips RHF validation, collects current values. */
        submitDraft: () => {
            const data = getValues();
            handleCreate(data);
        },
        /**
         * Has the user actually edited anything? `reset()` on template load clears this, and
         * autofill writes with a plain `setValue` (no `shouldDirty`), so this stays false for a
         * form that was merely opened. Callers use it to decide whether an auto-save is warranted.
         */
        isDirty: () => isDirty,
        /**
         * AI streaming entry point. Path is the full RHF dotted path
         * (`sections.X.attributes.Y.value`); `watch(path)` reflects the
         * write so RICH_TEXT re-renders live as chunks arrive. New AI content
         * invalidates any prior acceptance of the attribute's old content.
         */
        setFieldByPath: (path, value) => {
            if (!path) return false;
            setValue(path, value, { shouldDirty: true, shouldValidate: false });
            if (path.endsWith('.value')) {
                setValue(`${path.slice(0, -'.value'.length)}.aiContentAccepted`, false, { shouldValidate: false });
            }
            return true;
        },
    }));

    useEffect(() => {
        if (formList) {
            if (isFromWatch.current) {
                isFromWatch.current = false;
                return;
            }
            isInitializing.current = true;
            reset({
                customForm: formList?.id,
                formId: formList?.id,
                ...formList,
                sections: seedLegacyAiAcceptance(formList?.sections, noteStatus),
            });
            applyAutoFill();
            requestAnimationFrame(() => {
                isInitializing.current = false;
            });
        }
    }, [formList]);

    const handleCreate = async (data) => {
        onSigUploadingChange?.(true);
        try {
            await uploadPendingSignatures(data);
        } finally {
            onSigUploadingChange?.(false);
        }
        if (statusRef?.current) {
            data.status = statusRef.current;
            statusRef.current = null;
        }
        handleSaveNote(data);
    };

    const handleUploadFile = async (file, name) => {
        // Progress-report image uploads reuse the CLINICAL_DOC purpose —
        // same bucket + key namespace as the form-builder image path.
        // See uploadFileWithPreview docstring for blob-URL lifecycle.
        const { previewUrl, storedFile } = uploadFileWithPreview(file, KeyPurpose.CLINICAL_DOC);
        setValue(name, {
            url: previewUrl,
            originalName: file.name,
            format: (file.name.split('.').pop() || '').toLowerCase(),
        });
        setUploadLoader(true);
        try {
            setValue(name, { ...(await storedFile), url: previewUrl });
        } catch (e) {
            setValue(name, null);
            throw e;
        } finally {
            setUploadLoader(false);
        }
    };

    /**
     * Drag-reorder of a section's attributes — the report-builder analog of
     * FormBody's layout editing. Reorders react-hook-form directly (the form
     * renders from `watch().sections`); the watch→formList sync then persists.
     *
     * Two droppable shapes, scoped per section/goal via `type` so items never
     * cross boundaries:
     *   - `report-attrs-<sIndex>`            plain section — reorder one array.
     *   - `report-goal-attrs-<tsIndex>-<sIndex>-<goalId>` goal block. A goal
     *     that owns its fields reorders its own list and its targets' answer
     *     arrays (stored by index) — no other goal moves. A legacy goal renders
     *     the shared template, so the template AND every goal/target answer
     *     array move in lockstep to keep answers aligned.
     */
    const handleAttributesDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination) return;
        if (source.droppableId !== destination.droppableId) return;
        if (source.index === destination.index) return;
        const from = source.index;
        const to = destination.index;

        // Move element `from`→`to` in an array (no-op for non-arrays / bad index).
        const moveInArray = (arr) => {
            if (!Array.isArray(arr)) return arr;
            const copy = Array.from(arr);
            if (from < 0 || from >= copy.length) return copy;
            const [m] = copy.splice(from, 1);
            copy.splice(to, 0, m);
            return copy;
        };

        // Re-anchor the Properties-panel selection to the moved field when the
        // reorder happened in the currently active section.
        const reanchorSelection = (secIndex) => {
            if (activeSection !== secIndex + 1 || selectedIndex === null) return;
            let next = selectedIndex;
            if (selectedIndex === from) next = to;
            else if (from < selectedIndex && to >= selectedIndex) next = selectedIndex - 1;
            else if (from > selectedIndex && to <= selectedIndex) next = selectedIndex + 1;
            if (next !== selectedIndex) setSelectedIndex(next);
        };

        // The form renders from `watch().sections`, so react-hook-form — not
        // `formList` — is the display source of truth. Reorder RHF directly
        // (deterministic, re-renders immediately); the existing watch→formList
        // sync then persists the new order. Going through setFormList + the
        // reset effect instead was timing-dependent: that effect's `isFromWatch`
        // guard could skip the re-seed, leaving RHF in the old order so the
        // dropped card snapped back to its original position.
        const current = getValues();

        // Plain (non-goal) section: `report-attrs-<sIndex>`.
        const plain = /^report-attrs-(\d+)$/.exec(source.droppableId);
        if (plain) {
            const sIndex = Number(plain[1]);
            const section = current?.sections?.[sIndex];
            if (!section) return;
            setValue(`sections.${sIndex}.attributes`, moveInArray(section.attributes), { shouldDirty: true });
            reanchorSelection(sIndex);
            return;
        }

        // Goal block: `report-goal-attrs-<tsIndex>-<sIndex>-<goalId>`.
        const goalMatch = /^report-goal-attrs-(\d+)-(\d+)-(.+)$/.exec(source.droppableId);
        if (goalMatch) {
            const sIndex = Number(goalMatch[2]);
            const gid = goalMatch[3];
            const owner = current?.sections?.[sIndex];
            if (isOwnedGoal(owner, gid)) {
                const g = owner.goals[gid];
                setValue(`sections.${sIndex}.goals.${gid}.attributes`, moveInArray(g.attributes), { shouldDirty: true });
                Object.keys(g.targets || {}).forEach((tid) => {
                    const t = g.targets[tid] || {};
                    if (Array.isArray(t.attributes)) {
                        setValue(`sections.${sIndex}.goals.${gid}.targets.${tid}.attributes`, moveInArray(t.attributes), {
                            shouldDirty: true,
                        });
                    }
                });
                if (selectedGoalId === gid) reanchorSelection(sIndex);
                return;
            }

            // Legacy goal: ONE shared template rendered once per goal, so the
            // reorder applies to every goal. Per-goal and per-target answers are
            // stored by index, so the template array AND every override array
            // must move in lockstep — otherwise answers shift onto the wrong field.
            const tsIndex = Number(goalMatch[1]);
            const section = current?.sections?.[tsIndex];
            if (!section) return;
            // Set each ARRAY path individually (like the non-goal path above) —
            // replacing the whole section object doesn't reliably re-order the
            // registered leaf fields, so the template order wouldn't update.
            // Template order drives what every goal renders; the per-goal and
            // per-target answer arrays move identically so answers stay aligned.
            setValue(`sections.${tsIndex}.attributes`, moveInArray(section.attributes), { shouldDirty: true });
            if (section.goals && typeof section.goals === 'object') {
                Object.keys(section.goals).forEach((gid) => {
                    const g = section.goals[gid] || {};
                    if (Array.isArray(g.attributes)) {
                        setValue(`sections.${tsIndex}.goals.${gid}.attributes`, moveInArray(g.attributes), { shouldDirty: true });
                    }
                    if (g.targets && typeof g.targets === 'object') {
                        Object.keys(g.targets).forEach((tid) => {
                            const t = g.targets[tid] || {};
                            if (Array.isArray(t.attributes)) {
                                setValue(
                                    `sections.${tsIndex}.goals.${gid}.targets.${tid}.attributes`,
                                    moveInArray(t.attributes),
                                    { shouldDirty: true },
                                );
                            }
                        });
                    }
                });
            }
            reanchorSelection(tsIndex);
        }
    };

    // const getSignature = async (name) => {
    //     const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
    //     if (sigCanvas?.current && signatureData && isDrawing) {
    //         const convertedFile = base64ToFile(signatureData, 'signature.png');
    //         if (convertedFile) {
    //             let formData = new FormData();
    //             formData.append('files', convertedFile);
    //             try {
    //                 const res = await UploadServices.UploadSignature(formData);
    //                 const info = {
    //                     file: {
    //                         ...res?.data,
    //                         name: 'signature',
    //                     },
    //                 };
    //                 setValue(name, info?.file);
    //             } catch (e) {
    //                 setUploadLoader(false);
    //             }
    //         }
    //     }
    // };

    const scrollToFirstError = (errors) => {
        if (!errors?.attributes && !errors?.sections) return;

        const flattenErrors = (data, path) => {
            if (data?.sections) {
                for (let sIndex = 0; sIndex < data?.sections.length; sIndex++) {
                    const section = data?.sections[sIndex];
                    if (!section) continue;

                    if (section.attributes) {
                        const attrPath = flattenErrors({ attributes: section?.attributes }, `${path || 'sections'}.${sIndex}.attributes`);
                        if (attrPath) return attrPath;
                    }
                }
            }

            if (data?.attributes) {
                const attributes = data.attributes;
                for (let i = 0; i < attributes.length; i++) {
                    const item = attributes[i];
                    if (!item) continue;

                    const currentPath = `${path ? path : 'attributes'}.${i}`;

                    if (item?.value) return `${currentPath}.value`;
                    if (item?.aiContentAccepted) return `${currentPath}.aiContentAccepted`;

                    if (item?.fields) {
                        for (let fIndex = 0; fIndex < item?.fields?.length; fIndex++) {
                            const f = item.fields[fIndex];
                            if (f?.value) return `${currentPath}.fields.${fIndex}.value`;
                        }
                    }

                    if (item.children) {
                        const childPath = flattenErrors({ attributes: item.children }, `${currentPath}.children`);
                        if (childPath) return childPath;
                    }
                }
            }
            return null;
        };

        const firstErrorField = flattenErrors(errors);

        if (firstErrorField) {
            // Or the first input nested under that value — a Session Data Table's
            // manual cells are named `<value>.<row>[.trials.<i>].manual.<column>`.
            const field = document.querySelector(`[name="${firstErrorField}"], [name^="${firstErrorField}."]`);
            if (field?.scrollIntoView) {
                field.scrollIntoView({ behavior: 'smooth', block: 'center' });
                field.focus({ preventScroll: true });
            } else {
                formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    };

    /**
     * The fields a condition reads: a goal's own when it owns its fields (they carry its
     * answers), the section's otherwise — legacy goals keep judging on the section's
     * fields, as the PDF and members portal do.
     */
    const conditionAttributes = (sectionIndex, goalId) => {
        const sections = watch()?.sections;
        return goalId && isOwnedGoal(sections?.[sectionIndex], goalId)
            ? goalAttributes(sections, sectionIndex, goalId)
            : sections?.[sectionIndex]?.attributes || [];
    };

    /** Visible to this user and editable, under the same staff-only and conditional rules `renderInputs` applies. */
    const isEditableForUser = (item, sectionIndex, goalId) => {
        if (isDetail) return false;
        if (item?.properties?.staffOnly && userInfo?.userType !== userTypeEnums.STAFF) return false;
        if (item?.properties?.conditions?.length) {
            const condResult = evaluateConditions(item.properties.conditions, conditionAttributes(sectionIndex, goalId));
            if (!condResult.visible || condResult.readOnly) return false;
        }
        return true;
    };

    /** A field staff fill in on this note — what the "X completed · Y to fill" line counts. */
    const isFieldToFill = (item, sectionIndex, goalId) => isFillableAttribute(item, aiBridge) && isEditableForUser(item, sectionIndex, goalId);

    /**
     * What "Show only fields to fill" keeps: the fields to fill, plus editable tables (filled cell by
     * cell, required cells block saving — hiding one would hide the error it raises).
     */
    const isShownWhenFilling = (item, sectionIndex, goalId) =>
        (isFillableAttribute(item, aiBridge) || isEditableTable(item)) && isEditableForUser(item, sectionIndex, goalId);

    /** `{ total, filled }` over every field to fill, goal blocks included. */
    const fillProgress = (() => {
        if (isDetail) return { total: 0, filled: 0 };
        const sections = watch()?.sections || [];
        let total = 0;
        let filled = 0;
        const count = (item, sIndex, valueName, goalId) => {
            if (!isFieldToFill(item, sIndex, goalId)) return;
            total += 1;
            if (isAttributeFilled(watch(valueName))) filled += 1;
        };
        sections.forEach((section, sIndex) => {
            if (!section?.goalSection) {
                section?.attributes?.forEach((item, index) => count(item, sIndex, `sections.${sIndex}.attributes.${index}.value`));
                return;
            }
            (formList?.sections?.[sIndex]?.selectedGoals || []).forEach((goal) => {
                goalAttributes(sections, sIndex, goal?.id).forEach((item, index) =>
                    count(item, sIndex, `sections.${sIndex}.goals.${goal?.id}.attributes.${index}.value`, goal?.id),
                );
            });
        });
        return { total, filled };
    })();
    const hideNotToFill = showOnlyFieldsToFill && !isDetail && !fromReport;

    /** Whether a section has anything to fill — with the toggle on, a section without one is left out. */
    const sectionHasFieldToFill = (section, sIndex) => {
        if (!section?.goalSection) return !!section?.attributes?.some((item) => isShownWhenFilling(item, sIndex));
        return (formList?.sections?.[sIndex]?.selectedGoals || []).some((goal) =>
            goalAttributes(watch()?.sections, sIndex, goal?.id).some((item) => isShownWhenFilling(item, sIndex, goal?.id)),
        );
    };

    const renderInputs = (_templateItem, index, sectionIndex, goal = null, targetId = null, width) => {
        const checkSection = sectionIndex || sectionIndex === 0;
        const goalId = goal?.id;
        // In a goal block the caller passes the goal's field: its own (owned
        // goal) or the template one, which a legacy goal's override merges onto.
        // A target's override merges on top either way.
        const goalEntry = goalId ? formList?.sections?.[sectionIndex]?.goals?.[goalId] : null;
        const goalOverride = goalEntry && !goalEntry.ownAttributes ? goalEntry.attributes?.[index] : null;
        const targetOverride = targetId ? goalEntry?.targets?.[targetId]?.attributes?.[index] : null;
        const item = mergeAttribute(mergeAttribute(_templateItem, goalOverride), targetOverride);

        let namePrefix = `attributes.${index}`;
        if (checkSection) {
            namePrefix = `sections.${sectionIndex}.attributes.${index}`;
        }
        if (goalId && targetId) {
            namePrefix = `sections.${sectionIndex}.goals.${goalId}.targets.${targetId}.attributes.${index}`;
        } else if (goalId) {
            namePrefix = `sections.${sectionIndex}.goals.${goalId}.attributes.${index}`;
        }

        const name = `${namePrefix}.value`;
        const label = item?.label;
        const renderRichTextEditor = (value, onChange) => (
            <RichTextElement
                item={item}
                value={value}
                onChange={onChange}
                readOnly={false}
                index={index}
                wrapperStyle={{ border: 'none' }}
                activeSection={checkSection ? sectionIndex + 1 : undefined}
                // Fresh signed URLs for rich-text image refs.
                // Server-delivered on the note / report response envelope.
                imageUrls={imageUrls || {}}
                // Register freshly-uploaded images into the shared
                // context so they survive this editor instance
                // unmounting before the first save round-trip.
                onImageUpload={(key, url) => setImageUrls?.((prev) => ({ ...prev, [key]: url }))}
            />
        );
        const renderTable = () => (
            <TableElement
                item={item}
                readOnly={readOnly}
                index={index}
                name={name}
                watch={watch}
                marginBottom={'0'}
                // Required cells: the rule is registered on `name` through
                // the form's control; `errors` marks the empty cells.
                validationControl={control}
                errors={errors}
                // A note is being filled: Read Only cells cannot be typed in.
                lockReadOnlyCells
                // The clicked cell, for Properties' table STYLING (never saved on the report).
                onCellSelect={
                    isDetail
                        ? undefined
                        : (cell) => setReportTableCell({ ...cell, attributeId: item?.id, goalId: goalId || null, targetId: targetId || null })
                }
                // Builder mode (no `control`): full structural editing —
                // add/delete rows & columns, cell types, merge/split.
                // `name` is `<prefix>.value`; persist the body there and
                // the table layout at the sibling `<prefix>.properties.table`.
                // Both live under the same RHF path this attribute renders
                // from (section / goal / target alike), so the write lands
                // in the right place regardless of nesting, and the `watch`
                // subscription re-renders + syncs it back to formList.
                onUpdate={(newTableProperties, newValue) => {
                    if (readOnly) return;
                    const prefix = name.replace(/\.value$/, '');
                    if (newValue !== undefined) {
                        setValue(name, newValue, { shouldDirty: true });
                    }
                    if (newTableProperties !== undefined) {
                        setValue(`${prefix}.properties.table`, newTableProperties, { shouldDirty: true });
                    }
                }}
            />
        );
        let required = item?.properties?.required;
        // AI Summary fields are RICH_TEXT-only and force readOnly so the user
        // cannot type into them — content arrives via AI streaming.
        // `properties.aiSummary` is an AiSummaryKind enum value (truthy) when set.
        // Gated by `aiBridge`: if the admin disabled the AI feature for this
        // org, old forms with aiSummary=true must fall back to a normal
        // editable RICH_TEXT rather than staying soft-bricked as read-only.
        const isAiSummary =
            aiBridge &&
            item?.type === attributeTypes.RICH_TEXT &&
            !!item?.properties?.aiSummary;
        // Manual AI Assist is the staff-triggered counterpart — only on RICH_TEXT
        // that's NOT already an aiSummary (mutually exclusive markers), and never
        // in detail/read-only context. Shows a button next to the field's label.
        const isAiAssist =
            !isDetail &&
            item?.type === attributeTypes.RICH_TEXT &&
            !isAiSummary &&
            !!item?.properties?.aiAssist &&
            aiBridge &&
            // An AI rewrite returns plain formatted text and would drop the inline fields.
            !hasInlineFields(item?.value);
        let readOnly = isDetail ? true : item?.properties?.readOnly;
        const isActive = !isDetail &&
            selectedIndex === index &&
            activeSection === sectionIndex + 1 &&
            (!goalId || selectedGoalId === goalId) &&
            (!targetId || selectedTargetId === targetId);
        const fileValue = watch(name);
        const isImage =
            fileValue &&
            /\.(svg|jpe?g|png|gif|webp|bmp)$/i.test(
                typeof fileValue === 'string'
                    ? fileValue
                    : fileValue?.url || fileValue?.originalName || fileValue?.name || ''
            );


        // Staff-only visibility: hide from non-staff users during form fill
        if (item?.properties?.staffOnly && userInfo?.userType !== userTypeEnums.STAFF) {
            return null;
        }

        // Conditional logic: evaluate rules against current section values
        if (item?.properties?.conditions?.length && checkSection) {
            const condResult = evaluateConditions(item.properties.conditions, conditionAttributes(sectionIndex, goalId));
            if (!condResult.visible) return null;
            if (condResult.required) required = true;
            if (condResult.readOnly && !isDetail) readOnly = true;
        }

        // Select the field, opening it in Properties (progress report builder).
        const selectField = () => {
            if (isDetail) return;
            if (sectionIndex !== undefined && sectionIndex !== null) {
                setActiveSection(sectionIndex + 1);
            }
            setSelectedIndex(index);
            setSelectedGoalId(goalId || null);
            setSelectedTargetId(targetId || null);
        };

        // A read-only field while filling: its value in a locked grey box, "Not available" when empty.
        // Staff cannot change it, so it has no control and no validation. Rich Text keeps its html.
        // In a progress report (`fromReport`) the box is clickable: selecting it opens Properties, where
        // turning off Read Only unlocks the field on this report only (the template is not changed).
        if (readOnly && !isDetail && !isAiSummary && isReadOnlyBoxType(item)) {
            return (
                <LockedField
                    key={index}
                    item={item}
                    value={watch(name) ?? item?.value}
                    name={name}
                    label={label}
                    required={required}
                    imageUrls={imageUrls}
                    control={control}
                    onUnlockClick={fromReport ? selectField : undefined}
                />
            );
        }

        return (
            <div
                key={index}
                name={name}
                // Read-only fields (template flag or a condition) show a not-allowed cursor while
                // filling; detail view is read-only throughout, so it keeps the normal cursor.
                // A progress report can select (and unlock) them, so it keeps the pointer.
                className={readOnly && !isDetail ? (fromReport ? 'report-builder-unlockable-field' : 'report-builder-readonly-field') : undefined}
                onClick={selectField}
            >
                {item?.type === attributeTypes.RICH_TEXT && (
                    <div style={{ borderBottom: '1px solid #9CA3AF', paddingBottom: '8px' }} className="report-builder-text-editor">
                        {label && (
                            <p
                                className="preview-creator-info-line-title"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    ...(getNestedError(errors, name)?.message ? { color: '#FE7070' } : {}),
                                }}
                            >
                                {`${label}${required ? '*' : ''}`}
                                {isAiSummary && aiInProgress && (
                                    <AutoAwesomeIcon
                                        titleAccess="AI generating…"
                                        sx={{
                                            fontSize: 16,
                                            color: 'primary.main',
                                            animation: 'aiSparkPulse 1.8s ease-in-out infinite',
                                            '@keyframes aiSparkPulse': {
                                                '0%, 100%': { opacity: 0.35, transform: 'scale(0.9)' },
                                                '50%': { opacity: 1, transform: 'scale(1.1)' },
                                            },
                                        }}
                                    />
                                )}
                                {isAiAssist && (
                                    <>
                                        <IconButton
                                            size="small"
                                            disabled={aiAssistInProgress}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openAiAssistFor(name);
                                            }}
                                            sx={{ p: 0.25 }}
                                            title="AI Assist — preview & apply changes"
                                        >
                                            {aiAssistTransformingPath === name ? (
                                                <CircularProgress
                                                    size={16}
                                                    thickness={5}
                                                    sx={{ color: 'primary.main' }}
                                                />
                                            ) : (
                                                <AutoAwesomeIcon
                                                    sx={{ fontSize: 18, color: '#7C3AED' }}
                                                />
                                            )}
                                        </IconButton>
                                        {aiAssistTransformingPath === name && (
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                sx={{
                                                    color: 'primary.main',
                                                    fontStyle: 'italic',
                                                    animation:
                                                        'aiThinkingFade 1.2s ease-in-out infinite',
                                                    '@keyframes aiThinkingFade': {
                                                        '0%, 100%': { opacity: 0.55 },
                                                        '50%': { opacity: 1 },
                                                    },
                                                }}
                                            >
                                                AI rewriting…
                                            </Typography>
                                        )}
                                        {aiAssistOriginalByPath[name] !== undefined &&
                                            aiAssistTransformingPath !== name && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAiAssistRevert(name);
                                                    }}
                                                    sx={{ p: 0.25 }}
                                                    title="Revert to original (before AI)"
                                                >
                                                    <UndoIcon
                                                        sx={{
                                                            fontSize: 16,
                                                            color: 'text.secondary',
                                                        }}
                                                    />
                                                </IconButton>
                                            )}
                                    </>
                                )}
                            </p>
                        )}

                        {/* One rule for every editable variant (plain, inline fields, Goal Mode): the inline
                            fields' own checks, then `required` (`validateRichText`). Registered while the
                            editor is not open too, so an untouched required field still blocks the save. */}
                        <Controller
                            name={name}
                            control={control}
                            rules={{
                                validate: (value) => {
                                    if (isDetail || isAiSummary) return true;
                                    // Read-only text with inline fields: the fields are still filled, so still checked.
                                    if (readOnly && !(hasInlineFields(value) && !item?.properties?.goalMode)) return true;
                                    // Goal Mode: until a goal is picked the value is still the bare template.
                                    const goalIds = getValues(`${name.replace(/\.value$/, '')}.goalModeGoalIds`);
                                    if (required && item?.properties?.goalMode && !goalIds?.length) return richTextRequiredError(label);
                                    return validateRichText(value, {
                                        label,
                                        required,
                                        skipStaffOnly: userInfo?.userType !== userTypeEnums.STAFF,
                                    });
                                },
                            }}
                            render={({ field, fieldState: { error } }) => {
                                const withInlineFields = hasInlineFields(watch(name) ?? item.value) && !item?.properties?.goalMode;
                                // Re-checked on change only once the submit has flagged it: an inline control shows a mistyped value itself.
                                const handleChange = (html) => setValue(name, html, { shouldDirty: true, shouldValidate: !!error });
                                return (
                                    <>
                                        {withInlineFields ? (
                                            // Inline fields (Input / Date / Select / Checkbox / Radio inside the text):
                                            // staff fill the fields; a read-only note shows their values as text.
                                            readOnly && !isDetail && !isAiSummary ? (
                                                // Read-only text: drawn as it is, each inline field still a control staff fill.
                                                <InlineFieldsRichText
                                                    html={field.value ?? item.value}
                                                    onChange={handleChange}
                                                    hideStaffOnly={userInfo?.userType !== userTypeEnums.STAFF}
                                                    showErrors={!!error}
                                                    imageUrls={imageUrls || {}}
                                                />
                                            ) : readOnly ? (
                                                <div
                                                    className="richtext-readonly-content"
                                                    dangerouslySetInnerHTML={{
                                                        __html: sanitizeHtml(
                                                            resolveRichTextImages(
                                                                inlineFieldsToText(watch(name) ?? item.value, {
                                                                    hideStaffOnly: userInfo?.userType !== userTypeEnums.STAFF,
                                                                }),
                                                                imageUrls,
                                                            ),
                                                        ),
                                                    }}
                                                />
                                            ) : (
                                                // The editor with each field as its control: staff type and format the text too.
                                                <RichTextElement
                                                    item={item}
                                                    value={field.value ?? item.value}
                                                    onChange={handleChange}
                                                    readOnly={false}
                                                    wrapperStyle={{ border: 'none' }}
                                                    activeSection={checkSection ? sectionIndex + 1 : undefined}
                                                    imageUrls={imageUrls || {}}
                                                    onImageUpload={(key, url) => setImageUrls?.((prev) => ({ ...prev, [key]: url }))}
                                                    fill={{
                                                        hideStaffOnly: userInfo?.userType !== userTypeEnums.STAFF,
                                                        showErrors: !!error,
                                                    }}
                                                />
                                            )
                                        ) : item?.properties?.goalMode && !readOnly ? (
                                            // Goal Mode: the text filled once per goal staff pick.
                                            <GoalModeRichText
                                                item={item}
                                                name={name}
                                                watch={watch}
                                                getValues={getValues}
                                                setValue={setValue}
                                                renderEditor={renderRichTextEditor}
                                            />
                                        ) : readOnly || !isActive ? (
                                            (() => {
                                                const rawContent = watch(name) ?? item.value ?? '';
                                                const htmlContent =
                                                    typeof rawContent === 'string'
                                                        ? rawContent
                                                        : typeof rawContent === 'number'
                                                          ? String(rawContent)
                                                          : '';
                                                const strippedText = htmlContent.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                                                const hasContent = strippedText.length > 0 || /<img\s/i.test(htmlContent);
                                                return hasContent ? (
                                                    <div
                                                        style={{ cursor: readOnly ? 'default' : 'pointer', minHeight: 20 }}
                                                        className="richtext-readonly-content"
                                                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(resolveRichTextImages(htmlContent, imageUrls)) }}
                                                    />
                                                ) : (
                                                    <div
                                                        style={{ cursor: readOnly ? 'default' : 'pointer', minHeight: 20, color: '#98a2b3', fontSize: 12 }}
                                                        className="richtext-readonly-content"
                                                    >
                                                        {item?.placeholder || ''}
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            // shouldDirty: typing here must count toward `isDirty`, which drives the
                                            // unsaved-changes prompt. Safe on load — the editor's `onUpdate` only fires
                                            // on user edits (programmatic `setContent` passes emitUpdate=false).
                                            <div>{renderRichTextEditor(watch(name) || item.value, handleChange)}</div>
                                        )}
                                        {/* Inline-field errors are listed under the editor by the fields themselves. */}
                                        {error?.message && (!withInlineFields || error.message === richTextRequiredError(label)) && (
                                            <p className="error-message">{error.message}</p>
                                        )}
                                    </>
                                );
                            }}
                        />




                        {isAiSummary && !isDetail && (() => {
                            const aiFieldRaw = watch(name) ?? item.value ?? '';
                            const aiFieldValue =
                                typeof aiFieldRaw === 'string' ? aiFieldRaw : typeof aiFieldRaw === 'number' ? String(aiFieldRaw) : '';
                            const aiStripped = aiFieldValue.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                            const aiHasContent = aiStripped.length > 0 || /<img\s/i.test(aiFieldValue);
                            const checkboxDisabled = aiInProgress || !aiHasContent;
                            const acceptedName = `${namePrefix}.aiContentAccepted`;

                            return (
                                <Controller
                                    name={acceptedName}
                                    control={control}
                                    rules={{ validate: (v) => v === true || 'You must review and accept the AI-generated content.' }}
                                    render={({ field, formState: { errors: formErrors } }) => {
                                        const error = getNestedError(formErrors, acceptedName);
                                        return (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        disabled={checkboxDisabled}
                                                        checked={!!field.value}
                                                        onChange={(e) => field.onChange(e.target.checked)}
                                                        style={{
                                                            color: checkboxDisabled
                                                                ? '#D0D5DD'
                                                                : field.value
                                                                  ? '#0C66E4'
                                                                  : error
                                                                    ? '#FE7070'
                                                                    : '#98A2B3',
                                                            padding: '4px 8px',
                                                        }}
                                                        name={acceptedName}
                                                    />
                                                }
                                                label={
                                                    <span style={{
                                                        fontSize: 13,
                                                        color: checkboxDisabled ? '#98A2B3' : error ? '#FE7070' : '#344054',
                                                    }}>
                                                        I reviewed and accept the AI-generated content.*
                                                    </span>
                                                }
                                                sx={{ mt: 0.5, ml: 0 }}
                                            />
                                        );
                                    }}
                                />
                            );
                        })()}
                    </div>
                )}

                {item?.type === attributeTypes.INPUT && item?.defaultDateType === inputTypeEnums.TEXT && (
                    <Controller
                        name={name}
                        control={control}
                        rules={{
                            validate: (value) => {
                                if (!required) return true;
                                if (!value) return `${label} is required.`;
                                const stripped = value
                                    .replace(/<[^>]*>/g, '')
                                    .replace(/&nbsp;/g, ' ')
                                    .trim();
                                return stripped.length > 0 || `${label} is required.`;
                            },
                        }}
                        render={({ field, formState: { errors } }) => {
                            const error = getNestedError(errors, name);
                            return (
                                <div className="preview-info-line">
                                    {label && (
                                        <p className="preview-creator-info-line-title" style={error?.message ? { color: '#FE7070' } : {}}>
                                            {`${label}${required ? '*' : ''}`}
                                        </p>
                                    )}
                                    <MentionableInput
                                        className={`form-preview-input-box ${error ? 'error' : ''}`}
                                        defaultValue={field.value || watch(name)}
                                        value={field.value || watch(name)}
                                        onChange={(html) => {
                                            field.onChange(html);
                                        }}
                                        mentionOptions={currentModelEnums}
                                        readOnly={readOnly}
                                        noError={true}
                                        singleLine
                                        placeholder={item?.placeholder}
                                    />
                                </div>
                            );
                        }}
                    />
                )}

                {item?.type === attributeTypes.INPUT && item?.defaultDateType === inputTypeEnums.NUMBER && (
                    <div className={`report-builder-input-box ${getNestedError(errors, name)?.message ? 'error' : ''}`}>
                        {label && (
                            <p
                                className="preview-creator-info-line-title"
                                style={getNestedError(errors, name)?.message ? { color: '#FE7070' } : {}}
                            >
                                {`${label}${required ? '*' : ''}`}
                            </p>
                        )}
                        <Input
                            name={name}
                            // label={`${label}${required ? '*' : ''}`}
                            variant="standard"
                            control={control}
                            readOnly={readOnly}
                            placeholder={item?.placeholder}
                            rules={{
                                required: required ? `${label} is required.` : false,
                            }}
                        />
                    </div>
                )}

                {item?.type === attributeTypes.INPUT && item?.defaultDateType === inputTypeEnums.EMAIL && (
                    <div className={`report-builder-input-box ${getNestedError(errors, name)?.message ? 'error' : ''}`}>
                        {label && (
                            <p
                                className="preview-creator-info-line-title"
                                style={getNestedError(errors, name)?.message ? { color: '#FE7070' } : {}}
                            >
                                {`${label}${required ? '*' : ''}`}
                            </p>
                        )}
                        <Input
                            helperText={item?.description ? item?.description : ''}
                            name={name}
                            variant="standard"
                            type={'email'}
                            control={control}
                            readOnly={readOnly}
                            placeholder={item?.placeholder}
                            rules={{
                                required: required ? `${label} is required.` : false,
                            }}
                        />
                    </div>
                )}

                {item?.type === attributeTypes.INPUT && item?.defaultDateType === inputTypeEnums.PHONE && (
                    <div className={`report-builder-phone-input-box ${getNestedError(errors, name)?.message ? 'error' : ''}`}>
                        {label && (
                            <p
                                className="preview-creator-info-line-title"
                                style={getNestedError(errors, name)?.message ? { color: '#FE7070' } : {}}
                            >
                                {`${label}${required ? '*' : ''}`}
                            </p>
                        )}
                        <MaskInputCustom
                            noErrorMessage={true}
                            helperText={item?.description ? item?.description : ''}
                            name={name}
                            // label={`${label}${required ? '*' : ''}`}
                            variant="standard"
                            control={control}
                            readOnly={readOnly}
                            rules={{
                                required: required ? `${label} is required.` : false,
                                pattern: {
                                    value: OnlyNumber,
                                    message: 'Please enter a valid 10 digit number.',
                                },
                                minLength: {
                                    value: 10,
                                    message: 'Please enter a valid 10 digit number.',
                                },
                            }}
                        />
                    </div>
                )}

                {item?.type === attributeTypes.INPUT && item?.defaultDateType === inputTypeEnums.DATE && (
                    <div className={`report-builder-input-box ${getNestedError(errors, name)?.message ? 'error' : ''}`}>
                        {label && (
                            <p
                                className="preview-creator-info-line-title"
                                style={getNestedError(errors, name)?.message ? { color: '#FE7070' } : {}}
                            >
                                {`${label}${required ? '*' : ''}`}
                            </p>
                        )}
                        <DateCalendarForm
                            noShowIcons={!isActive}
                            noErrMessage={true}
                            name={name}
                            readOnly={readOnly}
                            control={control}
                            rules={{
                                required: required ? `${label} is required.` : false,
                            }}
                        />
                    </div>
                )}

                {item?.type === attributeTypes.INPUT && item?.defaultDateType === inputTypeEnums.TIME && (
                    <Controller
                        name={name}
                        control={control}
                        rules={{ required: required ? `${label} is required.` : false }}
                        render={({ formState: { errors } }) => {
                            const error = getNestedError(errors, name);
                            return (
                                <div className={`report-builder-input-box ${error?.message ? 'error' : ''}`}>
                                    {label && (
                                        <p className="preview-creator-info-line-title" style={error?.message ? { color: '#FE7070' } : {}}>
                                            {`${label}${required ? '*' : ''}`}
                                        </p>
                                    )}
                                    <TimeInput
                                        noShowIcons={!isActive}
                                        hhWidth={'25px'}
                                        mmWidth={'32px'}
                                        aPMWidth={'60px'}
                                        // labelColor={error?.message ? '#FE7070' : '#347AF0'}
                                        // label={`${label}${required ? '*' : ''}`}
                                        disabled={readOnly}
                                        name={name}
                                        typeError={error?.message}
                                        onChange={(ev) => setValue(name, ev?.target?.value)}
                                        defaultValue={
                                            watch(name) ? (watch(name)?.length > 10 ? renderTimes(watch(name)) : watch(name)) : null
                                        }
                                        errorFalse={true}
                                    />
                                </div>
                            );
                        }}
                    />
                )}

                {item?.type === attributeTypes.CHECK_BOX && !!item?.properties?.options?.length > 0 && (
                    <FormControl component="fieldset">
                        <Controller
                            name={name}
                            control={control}
                            defaultValue={Array.isArray(item?.value) ? item.value : []}
                            rules={buildSelectionRules(item, required, label)}
                            render={({ field, formState: { errors } }) => {
                                const error = getNestedError(errors, name);
                                const selected = Array.isArray(field.value) ? field.value : [];
                                const toggleOption = (option) => {
                                    const isSelected = selected.some((s) => s.id === option.id);
                                    const updated = isSelected
                                        ? selected.filter((s) => s.id !== option.id)
                                        : [...selected, { name: option.name, id: option.id }];
                                    field.onChange(updated);
                                };

                                return (
                                    <FormGroup>
                                        <p className="preview-creator-info-line-title" style={error?.message ? { color: '#FE7070' } : {}}>
                                            {`${label}${selectionRequiredMark(item, required)}`}
                                        </p>
                                        {item?.properties?.options?.map((option) => (
                                            <FormControlLabel
                                                className="report-radio-label"
                                                disabled={readOnly}
                                                key={option?.id}
                                                control={
                                                    <Checkbox
                                                        style={{
                                                            color: error?.message
                                                                ? '#FE7070'
                                                                : selected.some((s) => s.id === option.id)
                                                                  ? '#0C66E4'
                                                                  : '#D0D5DD',
                                                            padding: '2px 8px',
                                                        }}
                                                        checked={selected.some((s) => s.id === option.id)}
                                                        onChange={() => toggleOption(option)}
                                                    />
                                                }
                                                label={option.name}
                                            />
                                        ))}
                                        {error?.message && <p className="error-message">{error.message}</p>}
                                    </FormGroup>
                                );
                            }}
                        />
                    </FormControl>
                )}

                {item?.type === attributeTypes.RADIO_BUTTON && !!item?.properties?.options?.length > 0 && (
                    <FormControl component="fieldset">
                        <Controller
                            name={name}
                            control={control}
                            defaultValue={item?.value || null}
                            rules={{ required: item?.properties?.required ? `${item?.name} is required.` : false }}
                            render={({ field, formState: { errors } }) => {
                                const error = getNestedError(errors, name);
                                const currentValue = field.value?.id || '';

                                return (
                                    <RadioGroup
                                        {...field}
                                        value={currentValue}
                                        onChange={(e) => {
                                            const selectedId = e.target.value;
                                            const selectedOption = item?.properties?.options?.find((opt) => opt.id === selectedId);
                                            field.onChange(
                                                selectedOption
                                                    ? {
                                                          name: selectedOption.name,
                                                          id: selectedOption.id,
                                                      }
                                                    : null
                                            );
                                        }}
                                    >
                                        <p className="preview-creator-info-line-title" style={error?.message ? { color: '#FE7070' } : {}}>
                                            {`${label}${required ? '*' : ''}`}
                                        </p>
                                        {item?.properties?.options?.map((option) => (
                                            <FormControlLabel
                                                disabled={readOnly}
                                                className="report-radio-label"
                                                // className={classes.radioInputLabel}
                                                key={option.id}
                                                value={option.id}
                                                control={
                                                    <Radio
                                                        disableRipple
                                                        style={{
                                                            color: error?.message
                                                                ? '#FE7070'
                                                                : currentValue === option.id
                                                                  ? '#0C66E4'
                                                                  : '#D0D5DD',
                                                            padding: '2px 8px 2px 9px',
                                                        }}
                                                        classes={{
                                                            root: classes.radio,
                                                            checked: classes.checked,
                                                        }}
                                                    />
                                                }
                                                label={option.name}
                                            />
                                        ))}
                                    </RadioGroup>
                                );
                            }}
                        />
                    </FormControl>
                )}

                {item?.type === attributeTypes.SELECT &&
                    item?.defaultDateType === selectTypeEnums.MULTIPLE &&
                    !!item?.properties?.options?.length > 0 && (
                        <div
                            className={`builder-select-autocomplete show-error-text ${
                                getNestedError(errors, name)?.message ? 'error' : ''
                            }`}
                        >
                            <p
                                className="preview-creator-info-line-title"
                                style={getNestedError(errors, name)?.message ? { color: '#FE7070' } : {}}
                            >
                                {`${label}${selectionRequiredMark(item, required)}`}
                            </p>
                            <SelectAutocompleteMultiple
                                disableClearAble={!isActive}
                                defaultSelected={
                                    item?.value?.map((val) => ({
                                        name: val?.name,
                                        value: val?.value,
                                        id: val?.id,
                                    })) || []
                                }
                                multiple={true}
                                options={item?.properties?.options || []}
                                readOnly={readOnly}
                                name={name}
                                renderValue={(option) => option?.name || option?.value}
                                control={control}
                                variant="standard"
                                placeholder={item?.placeholder}
                                rules={buildSelectionRules(item, required, label)}
                            />
                        </div>
                    )}

                {item?.type === attributeTypes.SELECT &&
                    item?.defaultDateType === selectTypeEnums.SINGLE &&
                    !!item?.properties?.options?.length > 0 && (
                        <div
                            className={`builder-select-autocomplete show-error-text ${
                                getNestedError(errors, name)?.message ? 'error' : ''
                            }`}
                        >
                            <p
                                className="preview-creator-info-line-title"
                                style={getNestedError(errors, name)?.message ? { color: '#FE7070' } : {}}
                            >
                                {`${label}${required ? '*' : ''}`}
                            </p>
                            <SelectAutocompleteMultiple
                                disableClearAble={!isActive}
                                defaultSelected={item?.value && { name: item?.value?.name }}
                                options={item?.properties?.options || []}
                                readOnly={readOnly}
                                name={name}
                                renderValue={(option) => option?.name || option?.value}
                                control={control}
                                variant="standard"
                                // The picked option itself goes through `field.onChange`. A `setValue` after it (as
                                // before) replaced the value mid-validation, so react-hook-form dropped the result and
                                // a "required" error stayed after picking.
                                fullValue
                                multiline
                                placeholder={item?.placeholder}
                                rules={{
                                    required: required ? `${label} is required.` : false,
                                }}
                            />
                        </div>
                    )}

                {item?.type === attributeTypes.UPLOAD && (
                    <Controller
                        name={name}
                        control={control}
                        rules={{
                            required: required ? `${label} is required.` : false,
                        }}
                        render={({ field, formState: { errors } }) => {
                            const error = getNestedError(errors, name);
                            return (
                                <div>
                                    {isImage ? (
                                        <div className="builder-uploaded-image">
                                            <div
                                                className="space-between"
                                                style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
                                                <p
                                                    className="preview-creator-info-line-title"
                                                    style={error?.message ? { color: '#FE7070', marginBottom: 0 } : { marginBottom: 0 }}>
                                                    {`${label}${required ? '*' : ''}`}
                                                </p>
                                                {isActive ? (
                                                    <button
                                                        onClick={() => {
                                                            field.onChange(null);
                                                            setValue(name, null);
                                                            setSelectedIndex(null);
                                                        }}
                                                        className="reset-btn-by-default">
                                                        <img src={Images.redTrash} alt="redTrash" />
                                                    </button>
                                                ) : (
                                                    <div />
                                                )}
                                            </div>
                                            <div>
                                                <ResizableImage
                                                    handleClick={() => setSelectedIndex(index)}
                                                    src={fileValue?.url}
                                                    alt={'uploaded-image'}
                                                    style={{ maxHeight: 300 }}
                                                    initialWidth={fileValue?.resizedWidth}
                                                    initialHeight={fileValue?.resizedHeight}
                                                    onResize={({ width, height }) => {
                                                        setValue(name, {
                                                            ...fileValue,
                                                            resizedWidth: width,
                                                            resizedHeight: height,
                                                        });
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ) : !isDetail ? (
                                        <div>
                                            <p
                                                style={error?.message ? { color: '#FE7070' } : {}}
                                                className="preview-creator-info-line-title">
                                                {`${label}${required ? '*' : ''}`}
                                            </p>
                                            <FileUploader
                                                disabled={readOnly}
                                                handleChange={(file) => {
                                                    handleUploadFile(file, name);
                                                    clearErrors(name);
                                                }}
                                                name={name}>
                                                <div
                                                    className="drag-drop-component"
                                                    style={error?.message ? { borderColor: '#FE7070' } : {}}>
                                                    <div style={{ height: 50 }}>
                                                        {uploadLoader ? (
                                                            <MinLoader
                                                                margin={'0'}
                                                                width={'36px'}
                                                                height={'36px'}
                                                                color={'#475467'}
                                                                position={'relative'}
                                                                style={{ marginBottom: 12 }}
                                                            />
                                                        ) : (
                                                            <img src={Images.uploadCloudBlack} alt="icon" />
                                                        )}
                                                    </div>

                                                    <label className="custom-file-upload">
                                                        <input
                                                            disabled={readOnly}
                                                            onClick={(event) => (event.target.value = null)}
                                                            onChange={(file) => {
                                                                handleUploadFile(file, name);
                                                                clearErrors(name);
                                                            }}
                                                            type="file"
                                                            name={name}
                                                            id="file"
                                                        />
                                                        <i className="fa fa-cloud-upload" />
                                                        <div className="upload">
                                                            {!readOnly && (
                                                                <p>
                                                                    <span
                                                                        style={{
                                                                            color: '#2E6DD8',
                                                                            fontWeight: 600,
                                                                        }}>
                                                                        Click to upload
                                                                    </span>{' '}
                                                                    or drag and drop
                                                                </p>
                                                            )}
                                                            <p style={{ fontSize: '12px', color: '#172B4D' }}>
                                                                PDF, PNG, JPG and CSV (max. 800x400px)
                                                            </p>
                                                        </div>
                                                    </label>
                                                </div>
                                            </FileUploader>
                                            <p className="error-message">{error?.message}</p>
                                            {watch(name) && (
                                                <UploadedSignature
                                                    textWidth={width === 'calc(50% - 6px)' ? 170 : 330}
                                                    noDel={readOnly}
                                                    handleRemoveUploaded={() => {
                                                        field.onChange(null);
                                                        setValue(name, null);
                                                    }}
                                                    alreadyUploaded={true}
                                                    currentFile={{ file: watch(name) }}
                                                    alwaysDelete={true}
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <div>
                                            <p
                                                className="preview-creator-info-line-title"
                                                style={error?.message ? { color: '#FE7070' } : {}}>{`${label}${required ? '*' : ''}`}</p>
                                            <PrevUploadedFile item={{ value: fileValue || watch(name) }} />
                                        </div>
                                    )}
                                </div>
                            );
                        }}
                    />
                )}

                {item?.type === attributeTypes.SIGNATURE && isAutoSignature(item?.properties) && (
                    <div>
                        <p className="preview-creator-info-line-title">{label}</p>
                        <AutoSignatureElement item={item} />
                    </div>
                )}

                {item?.type === attributeTypes.SIGNATURE && !isAutoSignature(item?.properties) && (
                    <Controller
                        name={name}
                        control={control}
                        rules={{
                            required: required ? `${label} is required.` : false,
                        }}
                        render={({ field, formState: { errors } }) => {
                            const error = getNestedError(errors, name);

                            const showUploaded = formList?.formId ? !isActive : !!watch(name) || !!item?.value;

                            // Assigned-signer lock: when the field designates a specific signer
                            // (properties.signerUser), only that user may complete the signature.
                            // Everyone else gets a review-only, locked field.
                            const assignedSigner = item?.properties?.signerUser;
                            const isSignerLocked = !!assignedSigner?.id && userInfo?.id !== assignedSigner?.id;

                            return (
                                <div>
                                    {isDetail ?
                                        <div>
                                            <p className="preview-creator-info-line-title" style={error?.message ? { color: '#FE7070' } : {}}>
                                                {`${label}${required ? '*' : ''}`}
                                            </p>
                                            <PrevFileSignature item={{ value: watch(name) }} error={error?.message} />
                                        </div>
                                        :
                                    // {isActive && (
                                        <div>
                                            <p
                                                style={error?.message ? { color: '#FE7070' } : {}}
                                                className="preview-creator-info-line-title">
                                                {`${label}${required ? '*' : ''}`}
                                            </p>
                                            <FormSignature
                                                onSignatureChange={(pendingSignature) => {
                                                    field.onChange(pendingSignature);
                                                    setValue(name, pendingSignature);
                                                    if (pendingSignature) clearErrors(name);
                                                }}
                                                currentValue={watch(name) || item?.value}
                                                readOnly={readOnly || isSignerLocked}
                                                hasError={!!error?.message}
                                                signerLock={isSignerLocked ? { name: renderStaffName(assignedSigner) } : null}
                                            />
                                            <p className="error-message">{error?.message}</p>
                                        </div>
                                    }
                                </div>
                            );
                        }}
                    />
                )}

                {item?.type === attributeTypes.TABLE && (
                    // A read-only table keeps its grid, with grey cells and a lock by its label.
                    <div className={`builder-table-box${readOnly && !isDetail ? ' report-builder-locked-table' : ''}`}>
                        <p className="preview-creator-info-line-title">
                            {label}
                            {readOnly && !isDetail && <img src={Images.lockGray} alt="" className="report-builder-locked-box-icon" />}
                        </p>
                        {item?.properties?.goalMode && !readOnly ? (
                            <GoalModeTable
                                name={name}
                                watch={watch}
                                getValues={getValues}
                                setValue={setValue}
                                targetMode={!!item?.properties?.targetMode}
                            >
                                {renderTable()}
                            </GoalModeTable>
                        ) : (
                            renderTable()
                        )}
                    </div>
                )}

                {item?.type === attributeTypes.SESSION_DATA_TABLE && (
                    <div className="builder-table-box">
                        <p className="preview-creator-info-line-title">{label}</p>
                        {/* Columns are template config and AUTO cells come from the
                            session's collected data; only MANUAL columns take staff
                            input, written per row under `<name>.<row>.manual`.
                            `properties.readOnly` is ignored: blocks were created with it
                            set (the block used to be read-only) and authors cannot
                            change it, so only a read-only note locks the inputs. */}
                        <SessionDataTableElement item={item} index={index} readOnly={isDetail} control={control} errors={errors} name={name} />
                    </div>
                )}

                {item?.type === attributeTypes.GRAPH && (
                    <div className="graph-element-wrapper">
                        <p className="preview-creator-info-line-title">{label}</p>

                        <div className="form-builder-attribute-graph">
                            <GraphCharts item={item} sectionIndex={sectionIndex} goalId={goalId} />
                        </div>
                    </div>
                )}

                {item?.type === attributeTypes.TARGET && (
                    <TargetElement
                        item={item}
                        index={index}
                        value={watch(name)}
                        onChange={(val) => setValue(name, val)}
                        readOnly={readOnly}
                        isReportBuilder={true}
                        isActive={isActive}
                        sectionIndex={sectionIndex}
                        goalId={goalId}
                        targetId={targetId}
                        activeSection={checkSection ? sectionIndex + 1 : undefined}
                        imageUrls={imageUrls || {}}
                        onImageUpload={(key, url) => setImageUrls?.((prev) => ({ ...prev, [key]: url }))}
                    />
                )}
            </div>
        );
    };

    if (loadForm?.length || loadFormDefaults?.length) {
        return <Loader />;
    }

    // Header/Footer page rendering
    if (activeFormPage) {
        const pageKey = activeFormPage === formPageEnums.HEADER ? 'header' : 'footer';
        const pageData = formList?.[pageKey] || { content: '', properties: {} };

        const handleContentChange = (val) => {
            setFormList({
                ...formList,
                [pageKey]: {
                    ...pageData,
                    content: val,
                },
            });
        };

        const handleDeleteContent = () => {
            setFormList({
                ...formList,
                [pageKey]: {
                    ...pageData,
                    content: '',
                },
            });
        };

        return (
            <div className="flex-align-start full-width">
                <div className="form-body-wrapper">
                    <div className="form-body-info-banner">
                        <img
                            className="form-body-info-icon"
                            src={Images.fileAuth} alt="info"
                        />
                        <p>
                            Header and footer settings apply across the entire document and will appear on all generated pages.
                            Changes here are not limited to the currently selected section.
                        </p>
                    </div>

                    <div className="form-body-item-wrapper active-item form-body-hf-editor">
                        <div className="form-body-item-box">
                            <RichTextElement
                                item={{ type: attributeTypes.RICH_TEXT, value: pageData.content }}
                                value={pageData.content}
                                onChange={handleContentChange}
                                readOnly={false}
                                index={0}
                                imageUrls={imageUrls || {}}
                                onImageUpload={(key, url) => setImageUrls?.((prev) => ({ ...prev, [key]: url }))}
                            />
                        </div>
                        {pageData.content && (
                            <div className="form-body-hf-delete-row">
                                <button
                                    className="form-body-hf-delete-btn"
                                    onClick={handleDeleteContent}
                                >
                                    <img src={Images.trash} alt="delete" />
                                    <span className="form-body-hf-delete-text">Delete</span>
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="form-body-hf-preview-wrapper">
                        <p className="form-body-hf-preview-title">Preview</p>
                        <div className="form-preview-page form-body-hf-preview-page">
                            <PrevHeaderFooterBlock
                                type={pageKey}
                                data={{ ...pageData, show: true }}
                                pageIndex={0}
                                totalPages={formList?.sections?.length || 1}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Continuous (the default): every section flows on one sheet with a single header
    // and footer. Off: each section is its own page, as in the PDF.
    const continuousSections = isContinuousSections(formList?.formSettings);
    const wrapSectionsInPage = (sectionNodes) =>
        continuousSections ? (
            <div className="form-preview-page">
                <PrevHeaderFooterBlock type="header" data={watch('header') || formList?.header} pageIndex={0} totalPages={1} />
                {sectionNodes}
                <PrevHeaderFooterBlock type="footer" data={watch('footer') || formList?.footer} pageIndex={0} totalPages={1} />
            </div>
        ) : (
            sectionNodes
        );

    return (
        <div>
            {/* Note fill hosts only: the progress report builder (`fromReport`) has no fill bar. */}
            {!isDetail && !fromReport && fillProgress.total > 0 && (
                <div className="report-builder-fill-bar">
                    <span className="report-builder-fill-count">
                        {fillProgress.filled} completed · <b>{fillProgress.total - fillProgress.filled} to fill</b>
                    </span>
                    <span className="report-builder-fill-toggle">
                        Show only fields to fill
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={showOnlyFieldsToFill}
                                onChange={(e) => setShowOnlyFieldsToFill(e.target.checked)}
                            />
                            <span className="slider"></span>
                        </label>
                    </span>
                </div>
            )}
            <div className={fromReport ? "form-preview-container" : 'form-paper-preview-container' }>
                <DragDropContext onDragEnd={handleAttributesDragEnd}>
                <form
                    onSubmit={handleSubmit(
                        (data) => handleCreate(data),
                        (errors) => {
                            scrollToFirstError(errors);
                        }
                    )}
                    style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                    {wrapSectionsInPage(watch()?.sections?.map((section, sIndex) => {

                        const totalSections = watch()?.sections?.length || 1;
                        const sectionData = formList?.sections?.[sIndex];
                        const hasSelectedGoals = sectionData?.goalSection && sectionData?.selectedGoals?.length > 0;
                        if (!section?.attributes?.length && !hasSelectedGoals) return null;
                        if (section?.goalSection && !formList?.sections?.[sIndex]?.selectedGoals?.length && !hasSelectedGoals) return null;
                        if (hideNotToFill && !sectionHasFieldToFill(section, sIndex)) return null;

                        // if (sectionData?.goalSection && !hasSelectedGoals) return null;

                        return (
                            <div
                                key={sIndex}
                                className={continuousSections ? 'form-preview-continuous-section' : 'form-preview-page'}
                                ref={(el) => {
                                    if (sectionRefs) sectionRefs.current[sIndex] = el;
                                }}
                            >
                                {!continuousSections && (
                                    <PrevHeaderFooterBlock
                                        type="header"
                                        data={watch('header') || formList?.header}
                                        pageIndex={sIndex}
                                        totalPages={totalSections}
                                    />
                                )}

                                <div className="form-preview-section">
                                    {/* Once per section — a goal section's goal blocks below do not repeat it. */}
                                    {showSectionTitle(section) && (
                                        <div className={fromReport ? 'form-preview-section-heading' : undefined}>
                                            <p className="form-builder-section-title" style={sectionTitleStyle(section)}>{section?.name}</p>
                                            <div className="form-builder-section-line" />
                                        </div>
                                    )}
                                    {!section?.goalSection && (
                                        <Droppable droppableId={`report-attrs-${sIndex}`} type={`report-attrs-${sIndex}`}>
                                            {(dropProvided) => (
                                                <div
                                                    className="form-preview-attributes"
                                                    ref={dropProvided.innerRef}
                                                    {...dropProvided.droppableProps}
                                                >
                                                    {section?.attributes?.map((item, index) => {
                                                        // A read-only note leaves out blocks with nothing to show (same rule as the PDF).
                                                        if (isDetail && !isPrintableAttribute(item)) return null;
                                                        if (hideNotToFill && !isShownWhenFilling(item, sIndex)) return null;
                                                        const width = getWidth(item?.properties?.width);
                                                        const isFullWidthType = [attributeTypes.RICH_TEXT, attributeTypes.TABLE, attributeTypes.SESSION_DATA_TABLE, attributeTypes.GRAPH, attributeTypes.TARGET,].includes(item?.type);
                                                        const isActive = selectedIndex === index && activeSection === sIndex + 1;
                                                        // Live inputs must stay clickable, so only the grip handle drags
                                                        // (not the whole card, as in FormBody). Locked/read-only fields
                                                        // and detail (read-only) / fill-only hosts cannot be reordered.
                                                        const canReorder = allowReorder && !isDetail && !item?.properties?.updateForbidden;
                                                        const refKey = `${sIndex}-${index}`;
                                                        const frozenWidth = itemRefs.current[refKey]?.offsetWidth;
                                                        return (
                                                            <Draggable
                                                                key={item?.id || index}
                                                                draggableId={item?.id || `report-attr-${sIndex}-${index}`}
                                                                index={index}
                                                                isDragDisabled={!canReorder || onDragStart}
                                                            >
                                                                {(dragProvided, dragSnapshot) => (
                                                                    <div
                                                                        ref={(el) => {
                                                                            dragProvided.innerRef(el);
                                                                            itemRefs.current[refKey] = el;
                                                                        }}
                                                                        {...dragProvided.draggableProps}
                                                                        className={`form-preview-item ${isActive ? 'report-builder-active-item' : ''} ${dragSnapshot.isDragging ? 'report-builder-item-dragging' : ''}`}
                                                                        style={{
                                                                            ...dragProvided.draggableProps.style,
                                                                            width: dragSnapshot.isDragging
                                                                                ? frozenWidth
                                                                                : isFullWidthType
                                                                                ? '100%'
                                                                                : width,
                                                                        }}
                                                                    >
                                                                        {canReorder && (
                                                                            <span
                                                                                className="report-builder-drag-handle"
                                                                                title="Drag to reorder"
                                                                                {...dragProvided.dragHandleProps}
                                                                            >
                                                                                <img src={Images.dragIndicator} alt="Drag to reorder" />
                                                                            </span>
                                                                        )}
                                                                        {renderInputs(item, index, sIndex, '', '', width)}
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {dropProvided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    )}
                                    {section?.goalSection &&
                                        formList?.sections?.[sIndex]?.selectedGoals?.length > 0 &&
                                        (() => {
                                            const sectionData = formList?.sections?.[sIndex];
                                            const groupByDomain = sectionData?.groupByDomain;
                                            const showDomainLabels = sectionData?.showDomainLabels;
                                            const selectedGoals = sectionData?.selectedGoals || [];
                                            const watchedSections = watch()?.sections;
                                            const templateSection = goalTemplateSection(watchedSections, section);
                                            const templateSectionIndex =
                                                section?.attributes?.length > 0
                                                    ? sIndex
                                                    : watchedSections?.findIndex((s) => s?.goalSection && s?.attributes?.length > 0);
                                            const tsIndex = templateSectionIndex >= 0 ? templateSectionIndex : sIndex;

                                            const renderGoalBlock = (goal) => (
                                                <div
                                                    key={goal?.id}
                                                    className="goal-block-flat"
                                                    style={{ marginBottom: 16 }}
                                                    ref={(el) => {
                                                        if (goalRefs) goalRefs.current[`${sIndex}-${goal?.id}`] = el;
                                                    }}
                                                >
                                                    {/* Each goal block is its own Droppable (unique `type`) so a drag
                                                        stays within one block. A goal that owns its fields reorders
                                                        only itself; a legacy goal moves the shared template. */}
                                                    <Droppable
                                                        droppableId={`report-goal-attrs-${tsIndex}-${sIndex}-${goal?.id}`}
                                                        type={`report-goal-attrs-${tsIndex}-${sIndex}-${goal?.id}`}
                                                    >
                                                        {(dropProvided) => (
                                                            <div
                                                                className="form-preview-attributes"
                                                                ref={dropProvided.innerRef}
                                                                {...dropProvided.droppableProps}
                                                            >
                                                        {goalAttributes(watchedSections, sIndex, goal?.id).map((item, index) => {
                                                            // An owned goal's field is complete; a legacy one is the
                                                            // template field, which renderInputs merges the override onto.
                                                            const _templateItem = isOwnedGoal(sectionData, goal?.id)
                                                                ? item
                                                                : templateSection?.attributes?.[index];
                                                            if (isDetail && !isPrintableAttribute(item)) return null;
                                                            if (hideNotToFill && !isShownWhenFilling(item, sIndex, goal?.id)) return null;
                                                            const width = getWidth(item?.properties?.width);
                                                            const isFullWidthType = [
                                                                attributeTypes.RICH_TEXT,
                                                                attributeTypes.TABLE,
                                                                attributeTypes.SESSION_DATA_TABLE,
                                                                attributeTypes.GRAPH,
                                                                attributeTypes.TARGET,
                                                            ].includes(item?.type);
                                                            const isActive =
                                                                selectedIndex === index &&
                                                                activeSection === sIndex + 1 &&
                                                                selectedGoalId === goal?.id;
                                                            const isTargetGroup =
                                                                item?.type === attributeTypes.TARGET && goal?.targets?.length;
                                                            const canReorder = allowReorder && !isDetail && !item?.properties?.updateForbidden;
                                                            const refKey = `goal-${sIndex}-${goal?.id}-${index}`;
                                                            const frozenWidth = itemRefs.current[refKey]?.offsetWidth;
                                                            const cardWidth = isTargetGroup || isFullWidthType ? '100%' : width;
                                                            return (
                                                                <Draggable
                                                                    key={_templateItem?.id || index}
                                                                    draggableId={`goal-${goal?.id}-${_templateItem?.id || index}`}
                                                                    index={index}
                                                                    isDragDisabled={!canReorder || onDragStart}
                                                                >
                                                                    {(dragProvided, dragSnapshot) => (
                                                                        <div
                                                                            ref={(el) => {
                                                                                dragProvided.innerRef(el);
                                                                                itemRefs.current[refKey] = el;
                                                                            }}
                                                                            {...dragProvided.draggableProps}
                                                                            className={`form-preview-item ${!isTargetGroup && isActive ? 'report-builder-active-item' : ''} ${dragSnapshot.isDragging ? 'report-builder-item-dragging' : ''}`}
                                                                            style={{
                                                                                ...dragProvided.draggableProps.style,
                                                                                width: dragSnapshot.isDragging ? frozenWidth : cardWidth,
                                                                            }}
                                                                        >
                                                                            {canReorder && (
                                                                                <span
                                                                                    className="report-builder-drag-handle"
                                                                                    title="Drag to reorder"
                                                                                    {...dragProvided.dragHandleProps}
                                                                                >
                                                                                    <img src={Images.dragIndicator} alt="Drag to reorder" />
                                                                                </span>
                                                                            )}
                                                                            {isTargetGroup
                                                                                ? goal.targets.map((target) => {
                                                                                    const isTargetActive =
                                                                                        isActive && selectedTargetId === target?.id;
                                                                                    return (
                                                                                        <div
                                                                                            key={`${goal?.id}-target-${target?.id}-${index}`}
                                                                                            className={`form-preview-item ${isTargetActive ? 'report-builder-active-item' : ''}`}
                                                                                            style={{ width: '100%' }}
                                                                                        >
                                                                                            {renderInputs(_templateItem, index, sIndex, goal, target?.id)}
                                                                                        </div>
                                                                                    );
                                                                                })
                                                                                : renderInputs(_templateItem, index, sIndex, goal)}
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            );
                                                        })}
                                                                {dropProvided.placeholder}
                                                            </div>
                                                        )}
                                                    </Droppable>
                                                </div>
                                            );

                                            if (!groupByDomain) {
                                                return selectedGoals.map((goal) => renderGoalBlock(goal));
                                            }

                                            const domainGroups = {};
                                            const domainOrder = [];
                                            selectedGoals.forEach((goal) => {
                                                const domainId = goal?.domain?.id || '__no_domain__';
                                                const domainName = goal?.domain?.name || '(No Domain)';
                                                if (!domainGroups[domainId]) {
                                                    domainGroups[domainId] = { name: domainName, goals: [] };
                                                    domainOrder.push(domainId);
                                                }
                                                domainGroups[domainId].goals.push(goal);
                                            });

                                            const orderedDomainIds = [
                                                ...domainOrder.filter((id) => id !== '__no_domain__'),
                                                ...(domainOrder.includes('__no_domain__') ? ['__no_domain__'] : []),
                                            ];

                                            return orderedDomainIds.map((domainId) => {
                                                const group = domainGroups[domainId];
                                                return (
                                                    <div key={domainId} className="goal-domain-group">
                                                        {showDomainLabels && (
                                                            <div className="goal-domain-label">
                                                                <span
                                                                    className="goal-domain-label-prefix">Domain:</span>
                                                                <span
                                                                    className="goal-domain-label-name">{group.name}</span>
                                                            </div>
                                                        )}
                                                        {group.goals.map((goal) => renderGoalBlock(goal))}
                                                    </div>
                                                );
                                            });
                                        })()}
                                </div>

                                {/* Footer */}
                                {!continuousSections && (
                                    <PrevHeaderFooterBlock
                                        type="footer"
                                        data={watch('footer') || formList?.footer}
                                        pageIndex={sIndex}
                                        totalPages={totalSections}
                                    />
                                )}
                            </div>
                        );
                    }))}
                </form>
                </DragDropContext>
            </div>
            <AiAssistPopover
                open={!!aiAssistOpenPath}
                onClose={closeAiAssist}
                onGenerate={handleAiAssistGenerate}
                onApply={handleAiAssistApply}
                onRegenerate={handleAiAssistRegenerate}
                originalText={aiAssistOriginalSource}
                previewText={aiAssistPreviewText}
                phase={aiAssistPhase}
                errorMessage={aiAssistErrorMessage}
                lastInstruction={aiAssistLastInstruction}
            />
        </div>
    );
});
